import { useEffect, useRef, useState } from "react";

const usePositionObserver = (
  targetRef: React.RefObject<HTMLElement>,
  callback?: (rect?: DOMRect) => void
) => {
  const [rect, setRect] = useState<DOMRect>();
  const previousRect = useRef<DOMRect>();

  const checkPositionChange = (rect: DOMRect) => {
    if (
      !previousRect.current ||
      previousRect.current.top !== rect.top ||
      previousRect.current.left !== rect.left
    ) {
      callback?.(rect);
      setRect(rect);
    }
  };

  useEffect(() => {
    if (!targetRef.current) return;

    const intersectionObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting)
          checkPositionChange(entry.target.getBoundingClientRect());
      },
      { threshold: 0 }
    );

    const resizeObserver = new ResizeObserver((entries) => {
      if (entries[0])
        checkPositionChange(entries[0].target.getBoundingClientRect());
    });

    intersectionObserver.observe(targetRef.current);
    resizeObserver.observe(targetRef.current);

    return () => {
      if (targetRef.current) {
        intersectionObserver.unobserve(targetRef.current);
        resizeObserver.unobserve(targetRef.current);
        intersectionObserver.disconnect();
        resizeObserver.disconnect();
      }
    };
  }, [targetRef, callback]);

  return { rect };
};

export default usePositionObserver;
